import { useCallback, useEffect, useMemo, useRef, useState, type RefCallback } from 'react';
import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useDebounce } from '@/hooks/ui/useDebounce';
import { customRoleCatalogService } from '@/api/domains/catalog/customRoles/customRoles.service';
import { roleMembersService } from '@/api/domains/authorization/roleMembers.service';
import { mapRoleMember } from '@/api/domains/authorization/roleMembers.mapper';
import type { LC_SaveRoleMembersIn, RoleMember } from '@/api/domains/authorization/roleMembers.types';


const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;
const CANCEL_GUARD_DELAY_MS = SEARCH_DEBOUNCE_MS + 100;

// ── Return type interface ──────────────────────────────────────────────────

export interface UseUserProfilingResult {
  // Role selector
  roleOptions: { value: string; label: string }[];
  selectedRoleId: number | null;
  onRoleChange: (roleId: number | null) => void;

  // Non-members panel (left)
  nonMembers: RoleMember[];
  filteredNonMembers: RoleMember[];
  isNonMembersLoading: boolean;
  hasMoreNonMembers: boolean;
  totalNonMembers: number;
  nonMembersSearchText: string;
  onNonMembersSearch: (text: string) => void;
  nonMembersSentinelRef: RefCallback<HTMLDivElement>;
  nonMembersScrollContainerRef: RefCallback<HTMLDivElement>;

  // Members panel (right)
  members: RoleMember[];
  filteredMembers: RoleMember[];
  isMembersLoading: boolean;
  hasMoreMembers: boolean;
  totalMembers: number;
  membersSearchText: string;
  onMembersSearch: (text: string) => void;
  membersSentinelRef: RefCallback<HTMLDivElement>;
  membersScrollContainerRef: RefCallback<HTMLDivElement>;

  // Selection state — non-members
  selectedNonMemberIds: Set<number>;
  onToggleNonMember: (employeeId: number) => void;
  onSelectAllNonMembers: () => void;
  isAllNonMembersSelected: boolean;
  isNonMembersIndeterminate: boolean;

  // Selection state — members
  selectedMemberIds: Set<number>;
  onToggleMember: (employeeId: number) => void;
  onSelectAllMembers: () => void;
  isAllMembersSelected: boolean;
  isMembersIndeterminate: boolean;

  // Move operations
  onAdd: () => void;
  onRemove: () => void;
  onDoubleClickNonMember: (employee: RoleMember) => void;
  onDoubleClickMember: (employee: RoleMember) => void;
  onAddAllFromServer: () => void;
  onRemoveAllFromServer: () => void;
  isAddAllPending: boolean;
  isRemoveAllPending: boolean;

  // Save / cancel
  isDirty: boolean;
  isSaving: boolean;
  onSave: () => Promise<void>;
  onCancel: () => void;
}


// ── Hook ───────────────────────────────────────────────────────────────────

export function useUserProfiling(): UseUserProfilingResult {
  const { t: translate } = useTranslation();
  const { showNotification, showApiError } = useUI();

  // ── Role selector ─────────────────────────────────────────────────────
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [roleOptions, setRoleOptions] = useState<{ value: string; label: string }[]>([]);

  const loadRoleOptions = useCallback(async () => {
    const response = await customRoleCatalogService.getCustomRoles();
    if (response.success && response.data) {
      setRoleOptions(
        response.data.map((role) => ({ value: String(role.id), label: role.description })),
      );
    } else {
      showApiError(response);
    }
  }, [showApiError]);

  useEffect(() => { void loadRoleOptions(); }, [loadRoleOptions]);

  // ── Non-members panel — infinite scroll ───────────────────────────────
  const [nonMembers, setNonMembers] = useState<RoleMember[]>([]);
  const [isNonMembersLoading, setIsNonMembersLoading] = useState(false);
  const [hasMoreNonMembers, setHasMoreNonMembers] = useState(true);
  const [totalNonMembers, setTotalNonMembers] = useState(0);
  const nonMembersPageRef = useRef(1);
  const nonMembersAccumulatedRef = useRef(0);
  const latestNonMembersRequestRef = useRef(0);
  const fetchNonMembersRef = useRef<() => Promise<void>>(async () => {});
  const isFetchingNonMembersRef = useRef(false);
  const [nonMembersSentinelEl, setNonMembersSentinelEl] = useState<HTMLDivElement | null>(null);
  const [nonMembersContainerEl, setNonMembersContainerEl] = useState<HTMLDivElement | null>(null);

  const nonMembersSentinelRef = useCallback(
    (element: HTMLDivElement | null) => { setNonMembersSentinelEl(element); },
    [],
  );
  const nonMembersScrollContainerRef = useCallback(
    (element: HTMLDivElement | null) => { setNonMembersContainerEl(element); },
    [],
  );

  // ── Members panel — infinite scroll ───────────────────────────────────
  const [members, setMembers] = useState<RoleMember[]>([]);
  const [isMembersLoading, setIsMembersLoading] = useState(false);
  const [hasMoreMembers, setHasMoreMembers] = useState(true);
  const [totalMembers, setTotalMembers] = useState(0);
  const membersPageRef = useRef(1);
  const membersAccumulatedRef = useRef(0);
  const latestMembersRequestRef = useRef(0);
  const fetchMembersRef = useRef<() => Promise<void>>(async () => {});
  const isFetchingMembersRef = useRef(false);
  const [membersSentinelEl, setMembersSentinelEl] = useState<HTMLDivElement | null>(null);
  const [membersContainerEl, setMembersContainerEl] = useState<HTMLDivElement | null>(null);

  const membersSentinelRef = useCallback(
    (element: HTMLDivElement | null) => { setMembersSentinelEl(element); },
    [],
  );
  const membersScrollContainerRef = useCallback(
    (element: HTMLDivElement | null) => { setMembersContainerEl(element); },
    [],
  );

  // ── Search text ───────────────────────────────────────────────────────
  const [nonMembersSearchText, setNonMembersSearchText] = useState('');
  const [membersSearchText, setMembersSearchText] = useState('');
  const debouncedNonMembersSearchText = useDebounce(nonMembersSearchText, SEARCH_DEBOUNCE_MS);
  const debouncedMembersSearchText = useDebounce(membersSearchText, SEARCH_DEBOUNCE_MS);

  // ── Original state (for cancel) ───────────────────────────────────────
  const originalMembersRef = useRef<RoleMember[]>([]);
  const originalNonMembersRef = useRef<RoleMember[]>([]);
  const originalTotalMembersRef = useRef(0);
  const originalTotalNonMembersRef = useRef(0);
  const originalHasMoreMembersRef = useRef(false);
  // Guard: only store originals once per role selection, not on every members change
  const originalHasMoreNonMembersRef = useRef(false);
  const originalsStoredRef = useRef(false);
  // Guards to prevent each reset effect from clearing restored state during cancel.
  // Kept as separate refs (one per effect) so clearing one does not affect the other.
  // Cleared via setTimeout(>SEARCH_DEBOUNCE_MS) so they outlive the debounce window.
  const cancelNonMembersResetRef = useRef(false);
  const cancelMembersResetRef = useRef(false);

  // ── Selection state ───────────────────────────────────────────────────
  const [selectedNonMemberIds, setSelectedNonMemberIds] = useState<Set<number>>(new Set());
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<number>>(new Set());

  // ── Delta tracking (for save) ───────────────────────────────────────────
  const [addedIds, setAddedIds] = useState<Set<number>>(new Set());
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set());
  const [isAddAllPending, setIsAddAllPending] = useState(false);
  const [isRemoveAllPending, setIsRemoveAllPending] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Refs to read latest moved IDs inside async fetch callbacks without adding them to deps
  const addedIdsRef = useRef<Set<number>>(new Set());
  const removedIdsRef = useRef<Set<number>>(new Set());
  useEffect(() => { addedIdsRef.current = addedIds; }, [addedIds]);
  useEffect(() => { removedIdsRef.current = removedIds; }, [removedIds]);

  // Refs to read bulk-pending flags synchronously inside async callbacks
  const isAddAllPendingRef = useRef(false);
  const isRemoveAllPendingRef = useRef(false);
  useEffect(() => { isAddAllPendingRef.current = isAddAllPending; }, [isAddAllPending]);
  useEffect(() => { isRemoveAllPendingRef.current = isRemoveAllPending; }, [isRemoveAllPending]);

  // ── Fetch non-members ─────────────────────────────────────────────────
  const fetchNonMembers = useCallback(async () => {
    if (!selectedRoleId) return;
    if (isFetchingNonMembersRef.current) return;
    // Cuando "Quitar todos" está pendiente, el endpoint /non-members
    // devolvería datos desincronizados
    if (isRemoveAllPendingRef.current) return;

    isFetchingNonMembersRef.current = true;
    setIsNonMembersLoading(true);
    const requestId = ++latestNonMembersRequestRef.current;
    const page = nonMembersPageRef.current;

    const response = await roleMembersService.getNonMembers(
      selectedRoleId,
      {
        page,
        pageSize: PAGE_SIZE,
        ...(debouncedNonMembersSearchText ? { searchText: debouncedNonMembersSearchText } : {}),
      },
    );

    if (requestId !== latestNonMembersRequestRef.current) {
      isFetchingNonMembersRef.current = false;
      setIsNonMembersLoading(false);
      return;
    }

    if (response.success && response.data) {
      const totalRecords = response.data.pagination?.totalRecords ?? 0;
      const mappedData = (response.data.data ?? [])
        .map(mapRoleMember)
        .filter((e) => !addedIdsRef.current.has(e.employeeId));

      if (page === 1) {
        const adjustedTotal = totalRecords
          - addedIdsRef.current.size
          + removedIdsRef.current.size;
        setTotalNonMembers(adjustedTotal);
        nonMembersAccumulatedRef.current = mappedData.length;
        setNonMembers(mappedData);
      } else {
        nonMembersAccumulatedRef.current += mappedData.length;
        setNonMembers((previous) => [...previous, ...mappedData]);
      }

      setHasMoreNonMembers(
        mappedData.length > 0 && nonMembersAccumulatedRef.current < totalRecords,
      );
    } else {
      showApiError(response);
    }

    isFetchingNonMembersRef.current = false;
    setIsNonMembersLoading(false);
  }, [selectedRoleId, debouncedNonMembersSearchText, showApiError]);

  useEffect(() => { fetchNonMembersRef.current = fetchNonMembers; }, [fetchNonMembers]);

  // Reset pagination when search text changes so the next fetch starts from page 1.
  // Blocked when "Quitar todos" is pending to avoid fetching stale server data.
  // Also blocked during cancel so the restored originals are not overwritten.
  useEffect(() => {
    if (cancelNonMembersResetRef.current) return;
    if (isRemoveAllPending) return;
    nonMembersPageRef.current = 1;
    nonMembersAccumulatedRef.current = 0;
    isFetchingNonMembersRef.current = false;
    setNonMembers([]);
    setHasMoreNonMembers(true);
  }, [debouncedNonMembersSearchText, isRemoveAllPending]);

  useEffect(() => { void fetchNonMembers(); }, [fetchNonMembers]);

  useEffect(() => {
    // Scroll infinito bloqueado cuando "Quitar todos" está pendiente: el servidor devolvería
    // los datos originales sin reflejar la operación local no guardada.
    if (!nonMembersSentinelEl || !nonMembersContainerEl ||
      !hasMoreNonMembers || isRemoveAllPending) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNonMembersRef.current) {
          nonMembersPageRef.current += 1;
          void fetchNonMembersRef.current();
        }
      },
      { root: nonMembersContainerEl, threshold: 0 },
    );

    observer.observe(nonMembersSentinelEl);
    return () => observer.disconnect();
  }, [hasMoreNonMembers, nonMembersSentinelEl, nonMembersContainerEl, isRemoveAllPending]);

  // ── Fetch members ─────────────────────────────────────────────────────
  const fetchMembers = useCallback(async () => {
    if (!selectedRoleId) return;
    if (isFetchingMembersRef.current) return;
    // Cuando "Agregar todos" está pendiente, el endpoint /members devolvería datos desincronizados
    if (isAddAllPendingRef.current) return;

    isFetchingMembersRef.current = true;
    setIsMembersLoading(true);
    const requestId = ++latestMembersRequestRef.current;
    const page = membersPageRef.current;

    const response = await roleMembersService.getMembers(
      selectedRoleId,
      {
        page,
        pageSize: PAGE_SIZE,
        ...(debouncedMembersSearchText ? { searchText: debouncedMembersSearchText } : {}),
      },
    );

    if (requestId !== latestMembersRequestRef.current) {
      isFetchingMembersRef.current = false;
      setIsMembersLoading(false);
      return;
    }

    if (response.success && response.data) {
      const totalRecords = response.data.pagination?.totalRecords ?? 0;
      const mappedData = (response.data.data ?? [])
        .map(mapRoleMember)
        .filter((e) => !removedIdsRef.current.has(e.employeeId));

      if (page === 1) {
        const adjustedTotal = totalRecords
          - removedIdsRef.current.size
          + addedIdsRef.current.size;
        setTotalMembers(adjustedTotal);
        membersAccumulatedRef.current = mappedData.length;
        setMembers(mappedData);
      } else {
        membersAccumulatedRef.current += mappedData.length;
        setMembers((previous) => [...previous, ...mappedData]);
      }

      setHasMoreMembers(mappedData.length > 0 && membersAccumulatedRef.current < totalRecords);
    } else {
      showApiError(response);
    }

    isFetchingMembersRef.current = false;
    setIsMembersLoading(false);
  }, [selectedRoleId, debouncedMembersSearchText, showApiError]);

  useEffect(() => { fetchMembersRef.current = fetchMembers; }, [fetchMembers]);

  // Reset pagination when search text changes so the next fetch starts from page 1.
  // Blocked when "Agregar todos" is pending to avoid fetching stale server data.
  // Also blocked during cancel so the restored originals are not overwritten.
  useEffect(() => {
    if (cancelMembersResetRef.current) return;
    if (isAddAllPending) return;
    membersPageRef.current = 1;
    membersAccumulatedRef.current = 0;
    isFetchingMembersRef.current = false;
    setMembers([]);
    setHasMoreMembers(true);
  }, [debouncedMembersSearchText, isAddAllPending]);

  useEffect(() => { void fetchMembers(); }, [fetchMembers]);

  useEffect(() => {
    // Scroll infinito bloqueado cuando "Agregar todos" está pendiente: el servidor devolvería
    // los datos originales sin reflejar la operación local no guardada.
    if (!membersSentinelEl || !membersContainerEl || !hasMoreMembers || isAddAllPending) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingMembersRef.current) {
          membersPageRef.current += 1;
          void fetchMembersRef.current();
        }
      },
      { root: membersContainerEl, threshold: 0 },
    );

    observer.observe(membersSentinelEl);
    return () => observer.disconnect();
  }, [hasMoreMembers, membersSentinelEl, membersContainerEl, isAddAllPending]);

  // ── Role change — reset both panels ───────────────────────────────────
  const onRoleChange = useCallback((roleId: number | null) => {
    setSelectedRoleId(roleId);
    setNonMembers([]);
    setMembers([]);
    setSelectedNonMemberIds(new Set());
    setSelectedMemberIds(new Set());
    nonMembersPageRef.current = 1;
    nonMembersAccumulatedRef.current = 0;
    membersPageRef.current = 1;
    membersAccumulatedRef.current = 0;
    setHasMoreNonMembers(true);
    setHasMoreMembers(true);
    setTotalNonMembers(0);
    setTotalMembers(0);
    setAddedIds(new Set());
    setRemovedIds(new Set());
    setIsAddAllPending(false);
    setIsRemoveAllPending(false);
    originalMembersRef.current = [];
    originalNonMembersRef.current = [];
    originalsStoredRef.current = false;
    isFetchingNonMembersRef.current = false;
    isFetchingMembersRef.current = false;
    setNonMembersSearchText('');
    setMembersSearchText('');
    setIsDirty(false);
    // Pre-set loading states so store-originals effect doesn't fire with empty data
    if (roleId !== null) {
      setIsNonMembersLoading(true);
      setIsMembersLoading(true);
    }
  }, []);

  // Store originals exactly once after both panels finish loading for the selected role
  useEffect(() => {
    if (!selectedRoleId) return;
    if (isNonMembersLoading || isMembersLoading) return;
    if (originalsStoredRef.current) return;
    originalsStoredRef.current = true;
    originalMembersRef.current = members;
    originalNonMembersRef.current = nonMembers;
    originalTotalMembersRef.current = totalMembers;
    originalTotalNonMembersRef.current = totalNonMembers;
    originalHasMoreMembersRef.current = hasMoreMembers;
    originalHasMoreNonMembersRef.current = hasMoreNonMembers;
  }, [
    isNonMembersLoading, isMembersLoading, members, nonMembers,
    selectedRoleId, totalMembers, totalNonMembers, hasMoreMembers, hasMoreNonMembers,
  ]);

  // ── Client-side search filtering ──────────────────────────────────────
  // Provides interim results during the debounce window and filters locally moved items.
  const filteredNonMembers = useMemo(() => {
    const query = nonMembersSearchText.toLowerCase().trim();
    const withoutMoved = nonMembers.filter((e) => !addedIds.has(e.employeeId));
    if (!query) return withoutMoved;
    return withoutMoved.filter(
      (employee) =>
        employee.employeeName.toLowerCase().includes(query) ||
        employee.employeeTaxIdentificationNumber.toLowerCase().includes(query),
    );
  }, [nonMembers, nonMembersSearchText, addedIds]);

  const filteredMembers = useMemo(() => {
    const query = membersSearchText.toLowerCase().trim();
    const withoutMoved = members.filter((e) => !removedIds.has(e.employeeId));
    if (!query) return withoutMoved;
    return withoutMoved.filter(
      (employee) =>
        employee.employeeName.toLowerCase().includes(query) ||
        employee.employeeTaxIdentificationNumber.toLowerCase().includes(query),
    );
  }, [members, membersSearchText, removedIds]);

  // ── Selection handlers — non-members ─────────────────────────────────
  const onToggleNonMember = useCallback((employeeId: number) => {
    setSelectedNonMemberIds((previous) => {
      const next = new Set(previous);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  }, []);

  const isAllNonMembersSelected = useMemo(
    () =>
      filteredNonMembers.length > 0 &&
      filteredNonMembers.every((e) => selectedNonMemberIds.has(e.employeeId)),
    [filteredNonMembers, selectedNonMemberIds],
  );

  const isNonMembersIndeterminate = useMemo(
    () =>
      !isAllNonMembersSelected &&
      filteredNonMembers.some((e) => selectedNonMemberIds.has(e.employeeId)),
    [filteredNonMembers, selectedNonMemberIds, isAllNonMembersSelected],
  );

  const onSelectAllNonMembers = useCallback(() => {
    if (isAllNonMembersSelected) {
      setSelectedNonMemberIds((previous) => {
        const next = new Set(previous);
        filteredNonMembers.forEach((e) => next.delete(e.employeeId));
        return next;
      });
    } else {
      setSelectedNonMemberIds((previous) => {
        const next = new Set(previous);
        filteredNonMembers.forEach((e) => next.add(e.employeeId));
        return next;
      });
    }
  }, [filteredNonMembers, isAllNonMembersSelected]);

  // ── Selection handlers — members ─────────────────────────────────────
  const onToggleMember = useCallback((employeeId: number) => {
    setSelectedMemberIds((previous) => {
      const next = new Set(previous);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  }, []);

  const isAllMembersSelected = useMemo(
    () =>
      filteredMembers.length > 0 &&
      filteredMembers.every((e) => selectedMemberIds.has(e.employeeId)),
    [filteredMembers, selectedMemberIds],
  );

  const isMembersIndeterminate = useMemo(
    () => !isAllMembersSelected && filteredMembers.some((e) => selectedMemberIds.has(e.employeeId)),
    [filteredMembers, selectedMemberIds, isAllMembersSelected],
  );

  const onSelectAllMembers = useCallback(() => {
    if (isAllMembersSelected) {
      setSelectedMemberIds((previous) => {
        const next = new Set(previous);
        filteredMembers.forEach((e) => next.delete(e.employeeId));
        return next;
      });
    } else {
      setSelectedMemberIds((previous) => {
        const next = new Set(previous);
        filteredMembers.forEach((e) => next.add(e.employeeId));
        return next;
      });
    }
  }, [filteredMembers, isAllMembersSelected]);

  // ── Move operations ───────────────────────────────────────────────────
  const onAdd = useCallback(() => {
    const movedCount = selectedNonMemberIds.size;
    setAddedIds((previous) => {
      const next = new Set(previous);
      selectedNonMemberIds.forEach((id) => next.add(id));
      return next;
    });
    setRemovedIds((previous) => {
      const next = new Set(previous);
      selectedNonMemberIds.forEach((id) => next.delete(id));
      return next;
    });
    setNonMembers((previous) => previous.filter((e) => !selectedNonMemberIds.has(e.employeeId)));
    setMembers((previous) => [
      ...previous,
      ...nonMembers.filter((e) => selectedNonMemberIds.has(e.employeeId)),
    ]);
    setTotalNonMembers((previous) => previous - movedCount);
    setTotalMembers((previous) => previous + movedCount);
    setSelectedNonMemberIds(new Set());
    setIsDirty(true);
  }, [nonMembers, selectedNonMemberIds]);

  const onRemove = useCallback(() => {
    const movedCount = selectedMemberIds.size;
    setRemovedIds((previous) => {
      const next = new Set(previous);
      selectedMemberIds.forEach((id) => next.add(id));
      return next;
    });
    setAddedIds((previous) => {
      const next = new Set(previous);
      selectedMemberIds.forEach((id) => next.delete(id));
      return next;
    });
    setMembers((previous) => previous.filter((e) => !selectedMemberIds.has(e.employeeId)));
    setNonMembers((previous) => [
      ...previous,
      ...members.filter((e) => selectedMemberIds.has(e.employeeId)),
    ]);
    setTotalMembers((previous) => previous - movedCount);
    setTotalNonMembers((previous) => previous + movedCount);
    setSelectedMemberIds(new Set());
    setIsDirty(true);
  }, [members, selectedMemberIds]);

  const onDoubleClickNonMember = useCallback((employee: RoleMember) => {
    setAddedIds((previous) => {
      const next = new Set(previous);
      next.add(employee.employeeId);
      return next;
    });
    setRemovedIds((previous) => {
      const next = new Set(previous);
      next.delete(employee.employeeId);
      return next;
    });
    setNonMembers((previous) => previous.filter((e) => e.employeeId !== employee.employeeId));
    setMembers((previous) => [...previous, employee]);
    setTotalNonMembers((previous) => previous - 1);
    setTotalMembers((previous) => previous + 1);
    setSelectedNonMemberIds((previous) => {
      const next = new Set(previous);
      next.delete(employee.employeeId);
      return next;
    });
    setIsDirty(true);
  }, []);

  const onDoubleClickMember = useCallback((employee: RoleMember) => {
    setRemovedIds((previous) => {
      const next = new Set(previous);
      next.add(employee.employeeId);
      return next;
    });
    setAddedIds((previous) => {
      const next = new Set(previous);
      next.delete(employee.employeeId);
      return next;
    });
    setMembers((previous) => previous.filter((e) => e.employeeId !== employee.employeeId));
    setNonMembers((previous) => [...previous, employee]);
    setTotalMembers((previous) => previous - 1);
    setTotalNonMembers((previous) => previous + 1);
    setSelectedMemberIds((previous) => {
      const next = new Set(previous);
      next.delete(employee.employeeId);
      return next;
    });
    setIsDirty(true);
  }, []);

  // ── Server-wide bulk operations ─────────────────────────────────────────
  const onAddAllFromServer = useCallback(() => {
    setIsAddAllPending(true);
    setIsRemoveAllPending(false);
    setAddedIds(new Set());
    setRemovedIds(new Set());
    setMembers((previous) => [...previous, ...nonMembers]);
    setNonMembers([]);
    setTotalMembers((previous) => previous + totalNonMembers);
    setTotalNonMembers(0);
    setHasMoreNonMembers(false);
    // Si había más no-miembros en el servidor que los cargados,
    // el panel de destino ahora tiene más en servidor
    setHasMoreMembers(nonMembers.length < totalNonMembers);
    nonMembersPageRef.current = 1;
    nonMembersAccumulatedRef.current = 0;
    setSelectedNonMemberIds(new Set());
    setIsDirty(true);
  }, [nonMembers, totalNonMembers]);

  const onRemoveAllFromServer = useCallback(() => {
    setIsRemoveAllPending(true);
    setIsAddAllPending(false);
    setAddedIds(new Set());
    setRemovedIds(new Set());
    setNonMembers((previous) => [...previous, ...members]);
    setMembers([]);
    setTotalNonMembers((previous) => previous + totalMembers);
    setTotalMembers(0);
    setHasMoreMembers(false);
    // Si había más miembros en el servidor que los cargados,
    // el panel de destino ahora tiene más en servidor
    setHasMoreNonMembers(members.length < totalMembers);
    membersPageRef.current = 1;
    membersAccumulatedRef.current = 0;
    setSelectedMemberIds(new Set());
    setIsDirty(true);
  }, [members, totalMembers]);

  // ── Search handlers ───────────────────────────────────────────────────
  const onNonMembersSearch = useCallback((text: string) => {
    setNonMembersSearchText(text);
  }, []);

  const onMembersSearch = useCallback((text: string) => {
    setMembersSearchText(text);
  }, []);

  // ── Save ─────────────────────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);

  const onSave = useCallback(async () => {
    if (!selectedRoleId) return;
    setIsSaving(true);

    let body: LC_SaveRoleMembersIn;
    if (isAddAllPending) {
      body = { addAll: true, employeeIdsToRemove: removedIds.size > 0 ? [...removedIds] : null };
    } else if (isRemoveAllPending) {
      body = { removeAll: true, employeeIdsToAdd: addedIds.size > 0 ? [...addedIds] : null };
    } else {
      body = {
        employeeIdsToAdd: addedIds.size > 0 ? [...addedIds] : null,
        employeeIdsToRemove: removedIds.size > 0 ? [...removedIds] : null,
      };
    }

    const response = await roleMembersService.patchMembers(selectedRoleId, body);
    if (response.success) {
      showNotification('success', translate('authorization:userProfiling.messages.saveSuccess'), 5000);

      // Full reload of both panels so scroll state, totals and originals are in sync with the DB
      setNonMembers([]);
      setMembers([]);
      setSelectedNonMemberIds(new Set());
      setSelectedMemberIds(new Set());
      setAddedIds(new Set());
      setRemovedIds(new Set());
      setIsAddAllPending(false);
      setIsRemoveAllPending(false);
      nonMembersPageRef.current = 1;
      nonMembersAccumulatedRef.current = 0;
      membersPageRef.current = 1;
      membersAccumulatedRef.current = 0;
      setHasMoreNonMembers(true);
      setHasMoreMembers(true);
      isFetchingNonMembersRef.current = false;
      isFetchingMembersRef.current = false;
      originalsStoredRef.current = false;
      // Mutar refs directamente para que los guards en fetchNonMembers/fetchMembers
      // no bloqueen la recarga inmediata (los useEffects de sincronización aún no han corrido)
      isAddAllPendingRef.current = false;
      isRemoveAllPendingRef.current = false;
      setNonMembersSearchText('');
      setMembersSearchText('');
      setIsDirty(false);

      // Trigger re-fetch from page 1 for both panels
      await Promise.all([fetchNonMembersRef.current(), fetchMembersRef.current()]);
    } else {
      showApiError(response);
    }

    setIsSaving(false);
  }, [
    selectedRoleId, isAddAllPending, isRemoveAllPending,
    addedIds, removedIds, showApiError, showNotification, translate,
  ]);

  // ── Cancel ────────────────────────────────────────────────────────────
  const onCancel = useCallback(() => {
    // Activar los guards para que los effects de reset de paginación no sobreescriban
    // el estado restaurado. Se limpian con setTimeout > SEARCH_DEBOUNCE_MS (debounce) para que
    // también bloqueen cualquier disparo retrasado por el debounce del texto de búsqueda.
    cancelNonMembersResetRef.current = true;
    cancelMembersResetRef.current = true;
    setTimeout(() => {
      cancelNonMembersResetRef.current = false;
      cancelMembersResetRef.current = false;
    }, CANCEL_GUARD_DELAY_MS);
    setMembers(originalMembersRef.current);
    setNonMembers(originalNonMembersRef.current);
    setTotalMembers(originalTotalMembersRef.current);
    setTotalNonMembers(originalTotalNonMembersRef.current);
    setHasMoreMembers(originalHasMoreMembersRef.current);
    setHasMoreNonMembers(originalHasMoreNonMembersRef.current);
    nonMembersPageRef.current = Math.ceil(originalNonMembersRef.current.length / PAGE_SIZE) || 1;
    nonMembersAccumulatedRef.current = originalNonMembersRef.current.length;
    membersPageRef.current = Math.ceil(originalMembersRef.current.length / PAGE_SIZE) || 1;
    membersAccumulatedRef.current = originalMembersRef.current.length;
    setSelectedNonMemberIds(new Set());
    setSelectedMemberIds(new Set());
    setAddedIds(new Set());
    setRemovedIds(new Set());
    setIsAddAllPending(false);
    setIsRemoveAllPending(false);
    setNonMembersSearchText('');
    setMembersSearchText('');
    setIsDirty(false);
  }, []);

  return {
    roleOptions,
    selectedRoleId,
    onRoleChange,
    nonMembers,
    filteredNonMembers,
    isNonMembersLoading,
    hasMoreNonMembers,
    totalNonMembers,
    nonMembersSearchText,
    onNonMembersSearch,
    nonMembersSentinelRef,
    nonMembersScrollContainerRef,
    members,
    filteredMembers,
    isMembersLoading,
    hasMoreMembers,
    totalMembers,
    membersSearchText,
    onMembersSearch,
    membersSentinelRef,
    membersScrollContainerRef,
    selectedNonMemberIds,
    onToggleNonMember,
    onSelectAllNonMembers,
    isAllNonMembersSelected,
    isNonMembersIndeterminate,
    selectedMemberIds,
    onToggleMember,
    onSelectAllMembers,
    isAllMembersSelected,
    isMembersIndeterminate,
    onAdd,
    onRemove,
    onDoubleClickNonMember,
    onDoubleClickMember,
    onAddAllFromServer,
    onRemoveAllFromServer,
    isAddAllPending,
    isRemoveAllPending,
    isDirty,
    isSaving,
    onSave,
    onCancel,
  };
}

export default useUserProfiling;
